import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { useLocalStorage } from '@/hooks/useLocalStorage';

interface UseUrlCampaignSelectionResult {
    selectedCampaignId: string | null;
    setSelectedCampaignId: (campaignId: string | null) => void;
    clearSelectedCampaignId: () => void;
}

const STORAGE_KEY = 'selectedCampaignId';

/**
 * Custom hook to manage campaign selection state synced with URL parameters and localStorage.
 *
 * URL parameter is used to enable browser back/forward navigation and
 * localStorage is used to persist the selected campaign across page visits/refreshes,
 * so returning users see their last selected campaign.
 *
 * URL parameters take precedence over localStorage when both exist.
 */

const useCampaignSelection = (): UseUrlCampaignSelectionResult => {
    const [searchParams, setSearchParams] = useSearchParams();
    const campaignIdFromUrl = searchParams.get('projectId');

    const [storedCampaignId, setStoredCampaignId] = useLocalStorage<string | null>(STORAGE_KEY, null);

    // Determine the current campaign ID (URL takes precedence over localStorage)
    const selectedCampaignId = campaignIdFromUrl !== null ? campaignIdFromUrl : storedCampaignId;

    // Track if already synced on mount to prevent running on every dependency change
    const hasSyncedOnMount = useRef(false);

    // Sync URL with localStorage on mount if URL is empty but localStorage has a value
    useEffect(() => {
        if (hasSyncedOnMount.current) return;
        hasSyncedOnMount.current = true;

        if (campaignIdFromUrl === null && storedCampaignId !== null) {
            setSearchParams(
                prev => {
                    const newParams = new URLSearchParams(prev);
                    newParams.set('projectId', storedCampaignId);
                    return newParams;
                },
                { replace: true }
            );
        }
    }, [campaignIdFromUrl, storedCampaignId, setSearchParams]);

    // Only update localStorage when URL has a campaign ID (don't clear it when navigating away)
    useEffect(() => {
        if (campaignIdFromUrl !== null) {
            setStoredCampaignId(campaignIdFromUrl);
        }
    }, [campaignIdFromUrl, setStoredCampaignId]);

    const setSelectedCampaignId = useCallback(
        (projectId: string | null) => {
            setSearchParams(prev => {
                const newParams = new URLSearchParams(prev);

                if (projectId !== null) {
                    newParams.set('projectId', projectId);
                } else {
                    newParams.delete('projectId');
                    setStoredCampaignId(null);
                }

                // Reset pagination when project changes
                newParams.delete('page');

                return newParams;
            });
        },
        [setSearchParams, setStoredCampaignId]
    );

    const clearSelectedCampaignId = useCallback(() => {
        setSelectedCampaignId(null);
    }, [setSelectedCampaignId]);

    return {
        selectedCampaignId,
        setSelectedCampaignId,
        clearSelectedCampaignId,
    };
};

export default useCampaignSelection;
